/**
 * Dashboard home — stage 1: have a file, or start blank.
 * New / empty accounts see the ATS dropzone first (not a second hop).
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Seo from '../components/Seo';
import FeedbackModal from '../components/FeedbackModal';
import CVFileDropzone from '../components/CVFileDropzone';
import { identityLabel } from '../utils/displayName';
import { FROM_FILE, KEEP_LOOK, KEEP_LOOK_ENABLED, REWRITE } from '../utils/paths';
import './Dashboard.css';
import './FlowPages.css';

const Dashboard = () => {
  const { user, isPro, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState({});
  const [statsReady, setStatsReady] = useState(!isAuthenticated);
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setStatsReady(true);
      return undefined;
    }
    let cancelled = false;

    const fetchStats = async () => {
      try {
        const response = await axios.get('/api/cvs/stats/');
        if (!cancelled) {
          setStats(response.data);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        if (!cancelled) {
          setStatsReady(true);
        }
      }
    };

    fetchStats();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const userName = identityLabel(user) || 'Usuario';
  const analyses = stats.analyses_completed || 0;
  const uploads = stats.cvs_uploaded || 0;
  const hasActivity = isAuthenticated && (uploads > 0 || analyses > 0);
  const showDropzone = isAuthenticated && statsReady && !hasActivity;

  return (
    <div className="container dash">
      <Seo
        title={isAuthenticated ? t('nav.dashboard') : t('dashboard.guestHello')}
        description={isAuthenticated ? t('dashboard.lead') : t('dashboard.guestLead')}
        path="/dashboard"
        noindex
      />
      <header className="dash__header">
        <h1 className="dash__hello">
          {isAuthenticated
            ? t('dashboard.hello').replace('{name}', userName)
            : t('dashboard.guestHello')}
        </h1>
        <p className="dash__lead">
          {showDropzone
            ? t('dashboard.emptyLead')
            : isAuthenticated
              ? t('dashboard.lead')
              : t('dashboard.guestLead')}
        </p>
        {hasActivity ? (
          <p className="dash__meta">
            <span className={`dash__plan${isPro ? ' is-pro' : ''}`}>
              {isPro ? t('dashboard.planPro') : t('dashboard.planFree')}
            </span>
            <span>
              {uploads} {t('dashboard.cvsLabel')}
              <span aria-hidden="true"> · </span>
              {analyses} {t('dashboard.analysesLabel')}
            </span>
          </p>
        ) : null}
      </header>

      {showDropzone ? (
        <section className="dash__stage" aria-labelledby="dash-upload-title">
          <p className="dash__step">{t('dashboard.emptyStep')}</p>
          <h2 id="dash-upload-title" className="dash-primary__title">
            {t('dashboard.emptyTitle')}
          </h2>
          <p className="dash-primary__desc">{t('dashboard.emptyDesc')}</p>
          <CVFileDropzone source="dashboard" />
          <p className="dash__empty-alt">
            <Link to={REWRITE}>{t('dashboard.emptyRewrite')}</Link>
            {KEEP_LOOK_ENABLED ? (
              <>
                <span aria-hidden="true"> · </span>
                <Link to={KEEP_LOOK}>{t('dashboard.emptyKeepLook')}</Link>
              </>
            ) : null}
            <span aria-hidden="true"> · </span>
            <Link to="/dashboard/edit-cv">{t('dashboard.secondaryCta')}</Link>
          </p>
        </section>
      ) : (
        <section className="dash__stage" aria-labelledby="dash-primary-title">
          <p className="dash__step">{t('dashboard.stage1')}</p>
          <Link to={FROM_FILE} className="dash-primary">
            <div className="dash-primary__copy">
              <p className="dash-primary__eyebrow">{t('dashboard.primaryEyebrow')}</p>
              <h2 id="dash-primary-title" className="dash-primary__title">
                {t('dashboard.primaryTitle')}
              </h2>
              <p className="dash-primary__desc">{t('dashboard.primaryDesc')}</p>
            </div>
            <span className="dash-primary__cta">
              {t('dashboard.primaryCta')}
              <span aria-hidden="true">→</span>
            </span>
          </Link>

          <Link to="/dashboard/edit-cv" className="dash-secondary">
            <div className="dash-secondary__copy">
              <p className="dash-secondary__eyebrow">{t('dashboard.secondaryEyebrow')}</p>
              <h2 className="dash-secondary__title">{t('dashboard.secondaryTitle')}</h2>
              <p className="dash-secondary__desc">{t('dashboard.secondaryDesc')}</p>
            </div>
            <span className="dash-secondary__cta">
              {t('dashboard.secondaryCta')}
              <span aria-hidden="true">→</span>
            </span>
          </Link>
        </section>
      )}

      {!isPro && <p className="dash__quota">{t('dashboard.quotaFree')}</p>}

      {isAuthenticated ? (
        <>
          <button
            type="button"
            className="dash__feedback"
            onClick={() => setShowFeedback(true)}
          >
            <span>{t('feedback.openPrompt')}</span>
            <span className="dash__feedback-cta">
              {t('feedback.openCta')}
              <span aria-hidden="true"> →</span>
            </span>
          </button>
          <FeedbackModal
            open={showFeedback}
            onClose={() => setShowFeedback(false)}
            page="/dashboard"
          />
        </>
      ) : null}
    </div>
  );
};

export default Dashboard;
